/**
 * GlassCard primitive
 * Provides a reusable glassmorphism surface with configurable padding and elevation.
 */

import styled, { css } from 'styled-components';
import { colors } from '@config/seafoam_colors';
import { spacing, borderRadius, SpacingKey } from '@config/spacing';
import { transitions } from '@config/constants';

type Elevation = 'soft' | 'hard';

interface GlassCardProps {
  /**
   * Preset spacing token controlling internal padding.
   * Defaults to `2xl` to match the generous iOS-inspired basal rhythm.
   */
  $padding?: SpacingKey;
  /**
   * Elevation controls which shadow preset is used.
   */
  $elevation?: Elevation;
  /**
   * Optional flag to disable the hover lift effect when the card should remain static.
   */
  $disableHover?: boolean;
  /**
   * Optional flag to enable lift effect on hover without background darkening.
   * Useful for section containers that should feel interactive but not change color.
   */
  $liftOnly?: boolean;
}

const elevationStyles: Record<Elevation, string> = {
  soft: 'var(--glass-shadow-soft, 0 18px 40px rgba(30, 41, 59, 0.12))',
  hard: 'var(--glass-shadow-hard, 0 8px 24px rgba(30, 41, 59, 0.22))',
};

export const GlassCard = styled.div<GlassCardProps>`
  position: relative;
  z-index: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
  padding: ${({ $padding = '2xl' }) => spacing[$padding]};
  background: ${colors.glassSurface};
  border: 1px solid ${colors.glassBorder};
  border-radius: var(--glass-radius, ${borderRadius['3xl']});
  backdrop-filter: blur(var(--glass-blur, 20px));
  box-shadow: ${({ $elevation = 'soft' }) => elevationStyles[$elevation]}, inset 0 1px 0 rgba(255, 255, 255, 0.4);
  transition: all 200ms ${transitions.easing.easeInOut};
  overflow: hidden;

  ${({ $disableHover, $liftOnly }) =>
    !$disableHover &&
    css`
      &:hover {
        ${!$liftOnly && `background: ${colors.glassSurfaceHover};`}
        transform: translateY(-2px);
        box-shadow: ${elevationStyles.hard}, inset 0 1px 0 rgba(255, 255, 255, 0.5);
      }
    `}

  &::before,
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
  }

  &::before {
    background-image: url('/images/glass-noise.png');
    opacity: 0.05;
    z-index: 0;
  }

  &::after {
    border: 1px solid transparent;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0)) border-box;
    mask:
      linear-gradient(#fff 0 0) padding-box,
      linear-gradient(#fff 0 0);
    mask-composite: exclude;
    opacity: 0.4;
    z-index: 1;
  }

  & > * {
    position: relative;
    z-index: 2;
  }
`;

